import React, { useState, useEffect } from "react";
import {
  collection,
  getDocs,
  query,
  orderBy,
  doc,
  setDoc,
  doc as firestoreDoc,
  deleteDoc,
  getDoc,
  updateDoc,
  arrayUnion,
} from "firebase/firestore";
import { Timestamp } from "firebase/firestore";
import { db } from "../utils/firebase";
import { useNavigate } from "react-router-dom";
import { Users, ArrowLeft, X, ChevronDown, ListPlus } from "lucide-react";
import { Helmet } from "react-helmet";
import { useAuth } from "../hooks/AuthenticationProvider";
import { calculateProratedRent } from "../utils/rentCalculations";
import { findMedicationSuggestions } from "../utils/medicationList";

const formatDateForInput = (date) => {
  if (!date) return "";
  const d = new Date(date);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); // Adjust for local timezone
  return d.toISOString().split("T")[0];
};

const getTodayDateString = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return formatDateForInput(today);
};

const fetchWaitlistResidents = async (organizationId) => {
  const waitlistRef = collection(
    db,
    `organizations/${organizationId}/waitlist`
  );
  const q = query(waitlistRef, orderBy("dateAdded", "asc"));
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));
};

const calculateRentDetails = (moveInData, organizationData) => {
  const selectedHouse = organizationData?.housesData[moveInData.house];
  let houseFee = selectedHouse?.fee || 0;
  const moveInFee =
    parseInt(organizationData?.houseSettings?.moveInFee, 10) || 0;
  const rentSchedule =
    moveInData.rentSchedule || organizationData?.houseSettings?.rentSchedule;

  if (rentSchedule === "monthly") {
    houseFee = Number((houseFee * 4.33).toFixed(2));
  }

  return calculateProratedRent(
    moveInData.intakeDate,
    houseFee,
    moveInFee,
    rentSchedule,
    moveInData.rentDueDate
  );
};

const Waitlist = () => {
  const { user, isLoading, organizationId, organizationData } = useAuth();
  const navigate = useNavigate();
  const organizationName = organizationData?.organizationName || "";
  const housesData = organizationData?.housesData || {};
  const [isBreakdownOpen, setIsBreakdownOpen] = useState(false);
  const [waitlistResidents, setWaitlistResidents] = useState([]);
  const [error, setError] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedResident, setSelectedResident] = useState(null);
  const [waitlistLoading, setWaitlistLoading] = useState(true);
  const [referralOptions, setReferralOptions] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [createUserAccount, setCreateUserAccount] = useState(false);
  const [residentDashboardCount, setResidentDashboardCount] = useState(0);
  const [users, setUsers] = useState([]);
  const [newReferral, setNewReferral] = useState("");
  const [calculationDetails, setCalculationDetails] = useState(null);
  const [isMoveInModalOpen, setIsMoveInModalOpen] = useState(false);
  const [medicationSuggestions, setMedicationSuggestions] = useState([]);
  const [activeMedicationIndex, setActiveMedicationIndex] = useState(null);
  const [moveInData, setMoveInData] = useState({
    intakeDate: getTodayDateString(),
    balance: 0,
    uaCount: 3,
    currentResident: true,
    house: "",
    rentDueDate: "",
    rentSchedule: "",
  });
  const [sortFilter, setSortFilter] = useState({
    key: "dateAdded",
    direction: "asc",
  });

  const [activeTab, setActiveTab] = useState("housing");

  const STATUS_OPTIONS = [
    { value: "pending-bed", label: "Waiting for Bed", color: "yellow" },
    { value: "pending-jail", label: "In Jail/Prison", color: "red" },
    { value: "pending-treatment", label: "In Treatment", color: "blue" },
    { value: "pending-followup", label: "Awaiting Follow-up", color: "orange" },
  ];

  const ANALYTICS_FIELDS = {
    isMAT: {
      requiredType: "yesNo",
      options: ["True", "False"],
      displayName: "MAT Status",
      description: "Required for addiction treatment analytics",
    },
    drugOfChoice: {
      requiredType: "multipleChoice",
      options: [
        "THC",
        "Alcohol",
        "Fentanyl",
        "Cocaine",
        "Crack",
        "Meth",
        "Amphetamines",
        "Cough Syrup",
      ],
      maxSelections: 2,
      displayName: "Drug of Choice",
      description: "Required for substance abuse analytics",
    },
    workStatus: {
      requiredType: "dropdown",
      options: ["Unemployed", "Part-Time", "Full-Time", "Retired"],
      displayName: "Work Status",
      description: "Required for employment analytics",
    },
    workOnEntry: {
      requiredType: "dropdown",
      options: ["Unemployed", "Part-Time", "Full-Time", "Retired"],
      displayName: "Work Status on Entry",
      description: "Required for employment analytics",
    },
    veteranStatus: {
      requiredType: "dropdown",
      options: ["Veteran", "No military background"],
      displayName: "Veteran Status",
      description: "Required for veteran analytics",
    },
    gender: {
      requiredType: "dropdown",
      options: ["Male", "Female", "Trans", "Other"],
      displayName: "Gender",
      description: "Required for demographic analytics",
    },
  };

  const getSortIcon = (direction) => {
    return direction === "asc" ? (
      <span className="ml-1 inline-block">↑</span>
    ) : (
      <span className="ml-1 inline-block">↓</span>
    );
  };

  const formatPhoneNumber = (phoneNumber) => {
    // Remove any non-digit characters
    const cleaned = ("" + phoneNumber).replace(/\D/g, "");

    // Check if the input is of correct length
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);

    if (match) {
      return `${match[1]}-${match[2]}-${match[3]}`;
    }

    return phoneNumber; // Return original value if formatting fails
  };

  const formatDate = (date) => {
    if (!date) return "Not available";

    try {
      // Check if it's a Firestore Timestamp (has toDate method)
      if (date.toDate && typeof date.toDate === "function") {
        return date.toDate().toLocaleDateString();
      }
      // Check if it's already a Date object
      else if (date instanceof Date) {
        return date.toLocaleDateString();
      }
      // If it's a string or number, try to create a new Date
      else {
        const d = new Date(date);
        if (!isNaN(d.getTime())) {
          return d.toLocaleDateString();
        }
      }

      // If we got here, the date couldn't be parsed
      return "Invalid date";
    } catch (error) {
      console.warn("Error formatting date:", error, date);
      return "Error";
    }
  };

  const fetchUserData = async () => {
    if (!organizationId) return;

    try {
      // Fetch resident dashboard count
      const houseSettingsRef = firestoreDoc(
        db,
        `organizations/${organizationId}/houseSettings/initial`
      );
      const snapshot = await getDoc(houseSettingsRef);
      if (snapshot.exists()) {
        setResidentDashboardCount(
          snapshot.data().paidResDashboardCount ||
            snapshot.data().residentDashboardCount ||
            10
        );
      }

      // Fetch existing users
      const usersQuery = collection(
        db,
        "organizations",
        organizationId,
        "users"
      );
      const usersSnapshot = await getDocs(usersQuery);
      const fetchedUsers = usersSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setUsers(fetchedUsers);
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };

  // Add this useEffect to fetch user data when component mounts
  useEffect(() => {
    if (organizationId) {
      fetchUserData();
    }
  }, [organizationId]);

  // Utility functions (same as settings modal)
  const normalizeEmail = (email) => {
    return email?.toLowerCase().trim() || "";
  };

  const capitalizeFirstLetter = (str) => {
    return str?.charAt(0).toUpperCase() + str?.slice(1).toLowerCase() || "";
  };

  // Function to create user account
  const createResidentUser = async (residentData) => {
    if (!organizationId || !residentData.email) return false;

    try {
      // Normalize email and capitalize names
      const normalizedEmail = normalizeEmail(residentData.email);
      const capitalizedFirstName = capitalizeFirstLetter(
        residentData.firstName
      );
      const capitalizedLastName = capitalizeFirstLetter(residentData.lastName);

      // Check for duplicate users using normalized email
      const isDuplicateUser = users.some(
        (user) => normalizeEmail(user.email) === normalizedEmail
      );

      if (isDuplicateUser) {
        alert("User with this email already exists.");
        return false;
      }

      // Check resident dashboard limit
      const currentResidentCount = users.filter(
        (user) => user.role === "Resident"
      ).length;
      const maxResidents = residentDashboardCount || 10;

      if (currentResidentCount >= maxResidents) {
        alert(
          `Cannot create user account. Your resident limit is ${maxResidents} residents.`
        );
        return false;
      }

      // Create user document
      const userRef = firestoreDoc(
        db,
        "organizations",
        organizationId,
        "users",
        normalizedEmail
      );

      await setDoc(userRef, {
        email: normalizedEmail,
        firstName: capitalizedFirstName,
        lastName: capitalizedLastName,
        role: "Resident",
        addedBy: user.email,
        createdAt: new Date(),
        createdDuringMoveIn: true, // Flag to indicate this was created during move-in
      });

      console.log(
        `Created user account for ${capitalizedFirstName} ${capitalizedLastName}`
      );
      return true;
    } catch (error) {
      console.error("Error creating user account:", error);
      alert("Error creating user account: " + error.message);
      return false;
    }
  };

  // Fix for the getSortedResidents function
  const getSortedResidents = () => {
    // Create a copy of the residents array to avoid modifying the original
    const sortableResidents = [...waitlistResidents];

    sortableResidents.sort((a, b) => {
      let aValue, bValue;

      // Handle different sorting keys
      if (sortFilter.key === "dateAdded") {
        aValue = getTimestampValue(a.dateAdded);
        bValue = getTimestampValue(b.dateAdded);
      } else if (sortFilter.key === "intakeDate") {
        aValue = getTimestampValue(a.intakeDate);
        bValue = getTimestampValue(b.intakeDate);
      } else if (sortFilter.key === "name") {
        // Sort by full name (firstName + lastName)
        aValue = `${a.firstName || ""} ${a.lastName || ""}`.toLowerCase();
        bValue = `${b.firstName || ""} ${b.lastName || ""}`.toLowerCase();
        // For string comparison, we need to use localeCompare
        return sortFilter.direction === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else if (sortFilter.key === "house") {
        aValue = (a.house || "").toLowerCase();
        bValue = (b.house || "").toLowerCase();
        return sortFilter.direction === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else if (sortFilter.key === "status") {
        aValue = (a.status || "").toLowerCase();
        bValue = (b.status || "").toLowerCase();
        return sortFilter.direction === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      // For numeric values, use simple subtraction
      return sortFilter.direction === "asc" ? aValue - bValue : bValue - aValue;
    });

    return sortableResidents;
  };

  // Helper function to safely extract timestamp values
  const getTimestampValue = (timestamp) => {
    // Check if timestamp exists and is a Firestore Timestamp (has seconds property)
    if (timestamp && typeof timestamp === "object" && "seconds" in timestamp) {
      return timestamp.seconds;
    }

    // Check if timestamp is a Date object
    if (timestamp instanceof Date) {
      return timestamp.getTime() / 1000; // Convert to seconds for consistency
    }

    // Check if timestamp is a string that can be parsed to a date
    if (typeof timestamp === "string") {
      const date = new Date(timestamp);
      if (!isNaN(date.getTime())) {
        return date.getTime() / 1000;
      }
    }

    // Default value for missing or invalid timestamps
    return 0;
  };

  const handleSortToggle = (key) => {
    if (sortFilter.key === key) {
      // If already sorting by this key, toggle direction
      setSortFilter({
        key,
        direction: sortFilter.direction === "asc" ? "desc" : "asc",
      });
    } else {
      // Default to ascending for new sort key
      setSortFilter({ key, direction: "asc" });
    }

    // Important: Don't call any fetch functions here - just update the state
    // Let the UI update based on the existing data and new sort order
  };

  const handleMedicationChange = (index, field, value) => {
    // Make sure selectedResident and medications exist
    if (!selectedResident || !selectedResident.medications) {
      return;
    }

    // Create a new array of medications from the existing ones
    const updatedMedications = [...selectedResident.medications];

    // Ensure the medication at this index exists
    if (!updatedMedications[index]) {
      updatedMedications[index] = { name: "", dose: "" };
    }

    // Update the specific field (name or dose) with the new value
    updatedMedications[index][field] = value;

    // Update the selectedResident state with the modified medications array
    handleFieldChange("medications", updatedMedications);
  };

  const handleMedicationNameInput = (e, index) => {
    const value = e.target.value;
    handleMedicationChange(index, "name", value); // existing updater

    if (value.trim() === "") {
      setMedicationSuggestions([]);
      setActiveMedicationIndex(null);
      return;
    }

    setMedicationSuggestions(findMedicationSuggestions(value));
    setActiveMedicationIndex(index);
  };

  // Add this function to handle selecting a suggestion
  const handleSelectSuggestion = (suggestion, index) => {
    // store as "Brand (Generic)" – same format the IntakeForm uses
    const fullName = `${suggestion.brandName} (${suggestion.genericName})`;

    handleMedicationChange(index, "name", fullName); // <-- save formatted text
    setMedicationSuggestions([]);
    setActiveMedicationIndex(null);
  };

  const handleSortChange = (e) => {
    const value = e.target.value;
    const [key, direction] = value.split("-");
    setSortFilter({ key, direction });
  };

  const handleFieldChange = (field, value) => {
    // Special handling for date fields
    if (field === "dob" || field === "sobrietyDate") {
      if (value) {
        // Convert string date to Timestamp
        const [year, month, day] = value
          .split("-")
          .map((num) => parseInt(num, 10));
        const dateObj = new Date(year, month - 1, day); // month is 0-indexed
        setSelectedResident((prev) => ({
          ...prev,
          [field]: Timestamp.fromDate(dateObj),
        }));
      } else {
        // Handle empty value
        setSelectedResident((prev) => ({
          ...prev,
          [field]: null,
        }));
      }
    } else {
      // Handle non-date fields normally
      setSelectedResident((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending-bed":
        return "bg-yellow-500/10 text-yellow-400";
      case "pending-jail":
        return "bg-red-500/10 text-red-400";
      case "pending-treatment":
        return "bg-blue-500/10 text-blue-400";
      case "pending-followup":
        return "bg-orange-500/10 text-orange-400";
      default:
        return "bg-slate-500/10 text-slate-400";
    }
  };

  useEffect(() => {
    const fetchWaitlist = async () => {
      if (!organizationId) return;

      try {
        setWaitlistLoading(true);
        console.log("Fetching waitlist residents from Firestore...");

        const waitlistRef = collection(
          db,
          `organizations/${organizationId}/waitlist`
        );
        const q = query(waitlistRef, orderBy("dateAdded", "asc"));
        const querySnapshot = await getDocs(q);

        const residents = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        console.log(`Fetched ${residents.length} residents`);
        setWaitlistResidents(residents);
      } catch (err) {
        console.error("Error fetching waitlist:", err);
        setError("Failed to load waitlist");
      } finally {
        setWaitlistLoading(false);
      }
    };

    fetchWaitlist();
    // Only run this effect when organizationId changes, not on every render
  }, [organizationId]);

  // Add this right before your calculation setup
  useEffect(() => {
    if (
      !moveInData.house ||
      !moveInData.intakeDate ||
      !moveInData.rentDueDate ||
      !moveInData.rentSchedule
    ) {
      console.log("Missing required data for calculation");
      return;
    }

    try {
      const calculation = calculateRentDetails(moveInData, organizationData);
      setCalculationDetails(calculation);
      setMoveInData((prev) => ({
        ...prev,
        balance: calculation.totalBalance,
      }));
    } catch (error) {
      console.error("Error in calculation setup:", error);
    }
  }, [
    moveInData.intakeDate,
    moveInData.house,
    moveInData.rentDueDate,
    moveInData.rentSchedule,
    organizationData,
  ]);

  const fetchReferralOptions = async () => {
    if (!organizationId) return;

    try {
      const settingsRef = doc(
        db,
        `organizations/${organizationId}/houseSettings/initial`
      );
      const settingsSnap = await getDoc(settingsRef);

      if (settingsSnap.exists() && settingsSnap.data().referrals) {
        setReferralOptions(settingsSnap.data().referrals);
      } else {
        // Default options if none exist
        const defaultOptions = ["Self", "Jail", "Prison", "Respite", "Other"];
        await updateDoc(settingsRef, {
          referrals: defaultOptions,
        });
        setReferralOptions(defaultOptions);
      }
    } catch (error) {
      console.error("Error loading referral options:", error);
      setReferralOptions(["Self", "Jail", "Prison", "Respite", "Other"]);
    }
  };

  const handleAddReferralOption = async (newOption) => {
    if (!newOption || referralOptions.includes(newOption)) return;

    try {
      const settingsRef = doc(
        db,
        `organizations/${organizationId}/houseSettings/initial`
      );
      await updateDoc(settingsRef, {
        referrals: arrayUnion(newOption),
      });

      const updatedSnap = await getDoc(settingsRef);
      if (updatedSnap.exists()) {
        setReferralOptions(updatedSnap.data().referrals);

        // If we're in the process of updating a resident, set their referral location
        if (selectedResident && typeof selectedResident === "object") {
          setSelectedResident({
            ...selectedResident,
            referralLocation: newOption,
          });
        }
      }
    } catch (error) {
      console.error("Error adding referral option:", error);
    }
  };

  useEffect(() => {
    if (organizationId) {
      fetchReferralOptions();
    }
  }, [organizationId]);

  const handleDelete = async () => {
    if (!organizationId || !selectedResident) return;

    try {
      await deleteDoc(
        doc(db, `organizations/${organizationId}/waitlist`, selectedResident)
      );
      setWaitlistResidents((prev) =>
        prev.filter((resident) => resident.id !== selectedResident)
      );
      setDeleteModalOpen(false);
      setSelectedResident(null);
    } catch (error) {
      console.error("Error deleting resident:", error);
    }
  };

  const handleResidentSelect = (resident) => {
    setSelectedResident(resident);

    // Format existing rentDueDate with timezone consideration
    const existingRentDueDate = resident.rentDueDate
      ? formatDateForInput(resident.rentDueDate.toDate())
      : "";

    setMoveInData({
      intakeDate: getTodayDateString(),
      balance: 0,
      uaCount: resident.uaCount ?? 3,
      currentResident: true,
      house: resident.house || "",
      rentDueDate: existingRentDueDate,
      rentSchedule:
        resident.rentSchedule ||
        organizationData?.houseSettings?.rentSchedule ||
        "weekly",
    });

    setIsMoveInModalOpen(true);
    setActiveTab("housing"); // Reset to default tab
  };

  const handleStatusChange = async (residentId, newStatus, e) => {
    if (!organizationId) return;
    e.stopPropagation();

    try {
      await setDoc(
        doc(db, `organizations/${organizationId}/waitlist`, residentId),
        { status: newStatus },
        { merge: true }
      );

      setWaitlistResidents((prev) =>
        prev.map((resident) =>
          resident.id === residentId
            ? { ...resident, status: newStatus }
            : resident
        )
      );
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  const handleAddToWaitlist = () => {
    navigate("/intake");
  };

  // Add this function near your other handler functions
  const handleUpdate = async (e) => {
    if (!organizationId || !selectedResident) return;
    e.preventDefault();

    try {
      // Prepare update data - use existing resident data as base
      const updateData = {
        ...selectedResident, // Include ALL existing data first
        house: moveInData.house,
        uaCount: moveInData.uaCount,
        rentSchedule: moveInData.rentSchedule,
      };

      // Handle rentDueDate with timezone adjustment
      if (moveInData.rentDueDate) {
        // Parse the date string
        const [year, month, day] = moveInData.rentDueDate
          .split("-")
          .map((num) => parseInt(num, 10));
        // Create date object with the correct year, month, day values (month is 0-indexed)
        const rentDueDate = new Date(year, month - 1, day, 12, 0, 0, 0);
        updateData.rentDueDate = Timestamp.fromDate(rentDueDate);
      } else {
        updateData.rentDueDate = null;
      }

      // Handle intakeDate with timezone adjustment
      if (moveInData.intakeDate) {
        // Parse the date string
        const [year, month, day] = moveInData.intakeDate
          .split("-")
          .map((num) => parseInt(num, 10));
        // Create date object with the correct year, month, day values (month is 0-indexed)
        const intakeDate = new Date(year, month - 1, day, 12, 0, 0, 0);
        updateData.intakeDate = Timestamp.fromDate(intakeDate);
      } else {
        updateData.intakeDate = null;
      }

      console.log("Updating resident with data:", updateData);

      // Update the document in Firestore
      await setDoc(
        doc(
          db,
          `organizations/${organizationId}/waitlist`,
          selectedResident.id
        ),
        updateData
      );

      // Update the local state to reflect changes
      setWaitlistResidents((prev) =>
        prev.map((resident) =>
          resident.id === selectedResident.id
            ? { ...resident, ...updateData }
            : resident
        )
      );

      // Close the modal and reset state
      setIsMoveInModalOpen(false);
      setSelectedResident(null);
      setMoveInData({
        intakeDate: getTodayDateString(),
        balance: 0,
        uaCount: 3,
        currentResident: true,
        house: "",
        rentDueDate: "",
        rentSchedule: "",
      });

      // Show a success notification
      alert("Resident information updated successfully!");
    } catch (error) {
      console.error("Error updating resident:", error);
      alert("Error updating resident information: " + error.message);
    }
  };

  const handleMoveIn = async (e) => {
    if (!organizationId || !selectedResident) return;
    e.preventDefault();

    try {
      const calculation = calculateRentDetails(moveInData, organizationData);

      // Use the next due date from the calculation instead of the input date
      const nextDueDate = calculation.breakdown.nextDueDate;

      const selectedHouse = organizationData?.housesData[moveInData.house];
      const houseFee = selectedHouse?.fee || 0;
      const rentSchedule = moveInData.rentSchedule;

      // Get the base name without any counter
      const baseName = `${selectedResident.firstName} ${selectedResident.lastName}`;

      // Query to get all residents (to check for existing name patterns)
      const residentsRef = collection(
        db,
        `organizations/${organizationId}/residents`
      );
      const querySnapshot = await getDocs(residentsRef);

      // Find all matching residents and determine the highest counter
      let highestCounter = 0;
      querySnapshot.docs.forEach((doc) => {
        const resident = doc.data();
        const fullName = resident.fullName || "";

        // Check exact match or match with counter pattern
        if (fullName === baseName) {
          highestCounter = Math.max(highestCounter, 1);
        } else {
          // Check for name with counter pattern
          const match = fullName.match(
            new RegExp(`^${baseName} \\((\\d+)\\)$`)
          );
          if (match) {
            const counter = parseInt(match[1], 10);
            highestCounter = Math.max(highestCounter, counter);
          }
        }
      });

      // Determine the new full name
      const nameWithCount =
        highestCounter > 0 ? `${baseName} (${highestCounter + 1})` : baseName;

      const residentData = {
        ...selectedResident, // Base data from waitlist
        ...moveInData, // House/rent related data

        // Explicitly preserve important fields if they exist in selectedResident
        dob: selectedResident.dob || "",
        veteranStatus: selectedResident.veteranStatus || "",
        workStatus: selectedResident.workStatus || "",
        sobrietyDate: selectedResident.sobrietyDate || "",

        // These remain the same
        fullName: nameWithCount,
        intakeDate: moveInData.intakeDate,
        currentResident: true,
        balance: calculation.totalBalance,
        rentAmount: houseFee,
        rentDueDate: nextDueDate ? Timestamp.fromDate(nextDueDate) : null,
        rentSchedule: rentSchedule,
        uaCount: moveInData.uaCount,
        bedLocation: "",
        isWaitlist: false,
        house: moveInData.house,
      };

      delete residentData.status;
      delete residentData.dateAdded;

      // Create user account if checkbox is checked and email exists
      if (createUserAccount && selectedResident.email) {
        const userCreated = await createResidentUser(selectedResident);
        if (!userCreated) {
          // If user creation failed, ask if they want to continue without creating user
          const continueWithoutUser = window.confirm(
            "Failed to create user account. Would you like to continue with the move-in process without creating a user account?"
          );
          if (!continueWithoutUser) {
            return; // Stop the move-in process
          }
        }
      }

      // Save resident data and remove from waitlist
      await setDoc(
        firestoreDoc(
          db,
          `organizations/${organizationId}/residents`,
          selectedResident.id
        ),
        residentData
      );

      await deleteDoc(
        firestoreDoc(
          db,
          `organizations/${organizationId}/waitlist`,
          selectedResident.id
        )
      );

      // Reset modal and fetch updated waitlist
      setIsMoveInModalOpen(false);
      setSelectedResident(null);
      setCreateUserAccount(false); // Reset checkbox
      setMoveInData({
        intakeDate: new Date().toISOString().split("T")[0],
        balance: 0,
        uaCount: 3,
        currentResident: true,
        house: "",
        rentDueDate: "",
        rentSchedule: "",
      });

      // Refresh waitlist using helper function
      const residents = await fetchWaitlistResidents(organizationId);
      setWaitlistResidents(residents);

      // Show success message
      let successMessage = "Resident moved in successfully!";
      if (createUserAccount && selectedResident.email) {
        successMessage += " User account created.";
      }
      alert(successMessage);

      navigate("/beds");
    } catch (error) {
      console.error("Error moving resident in:", error);
      alert("Error during move-in process: " + error.message);
    }
  };

  useEffect(() => {
    if (!isLoading && !user) {
      navigate("/login");
    }
  }, [isLoading, user, navigate]);

  if (isLoading || waitlistLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-white"></div>
      </div>
    );
  }

  if (error) {
    return <div className="text-red-500 text-center mt-10">{error}</div>;
  }

  return (
    <>
      <Helmet>
        <title>• Waitlist</title>
      </Helmet>

      <div className="min-h-screen bg-[#18181B] bg-gradient-to-br from-zinc-900 via-zinc-900/95 to-zinc-950 p-6 relative overflow-hidden">
        {/* Ambient background effects */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(24,24,27,0.3),rgba(0,0,0,0))]" />
        <div className="absolute inset-0">
          <div className="absolute top-0 -left-4 w-96 h-96 bg-cyan-500/10 rounded-full mix-blend-screen filter blur-3xl animate-pulse" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full mix-blend-screen filter blur-3xl animate-pulse delay-700" />
        </div>

        <div className="max-w-7xl mx-auto space-y-8 relative">
          {/* Header */}
          <div className="flex items-center gap-6 mb-12">
            <div className="flex gap-3">
              <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse" />
              <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse delay-100 opacity-70" />
              <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse delay-200 opacity-40" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-white tracking-tight">
                Waitlist Management
              </h1>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-1 gap-4 mb-8">
            <div className="flex flex-col md:flex-row gap-4">
              <button
                onClick={() => navigate("/intake")}
                className="w-full md:w-auto px-6 py-3 bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white rounded-xl transition-all duration-300 shadow-lg hover:shadow-cyan-500/25 font-medium flex items-center justify-center gap-2"
              >
                <ListPlus size={20} />
                Add to Waitlist
              </button>
              <button
                onClick={() => navigate("/")}
                className="w-full md:w-auto px-6 py-3 bg-slate-800 hover:bg-slate-700 text-white rounded-xl transition-all duration-300 shadow-lg font-medium flex items-center justify-center gap-2"
              >
                <ArrowLeft size={20} />
                Back to Dashboard
              </button>
            </div>
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block group relative bg-slate-900/50 backdrop-blur-xl rounded-xl overflow-hidden hover:shadow-2xl transition-all duration-500">
            <div className="absolute inset-0 bg-gradient-to-b from-slate-800/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div className="relative overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-800/50">
                  <tr>
                    <th
                      className="px-6 py-4 text-left text-sm font-semibold text-slate-300 cursor-pointer hover:bg-slate-700/30 transition-colors"
                      onClick={() => handleSortToggle("name")}
                    >
                      Name{" "}
                      {sortFilter.key === "name" &&
                        getSortIcon(sortFilter.direction)}
                    </th>
                    <th
                      className="px-6 py-4 text-left text-sm font-semibold text-slate-300 cursor-pointer hover:bg-slate-700/30 transition-colors"
                      onClick={() => handleSortToggle("house")}
                    >
                      House{" "}
                      {sortFilter.key === "house" &&
                        getSortIcon(sortFilter.direction)}
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">
                      Phone
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">
                      Referral
                    </th>
                    <th
                      className="px-6 py-4 text-left text-sm font-semibold text-slate-300 cursor-pointer hover:bg-slate-700/30 transition-colors"
                      onClick={() => handleSortToggle("dateAdded")}
                    >
                      Added{" "}
                      {sortFilter.key === "dateAdded" &&
                        getSortIcon(sortFilter.direction)}
                    </th>
                    <th
                      className="px-6 py-4 text-left text-sm font-semibold text-slate-300 cursor-pointer hover:bg-slate-700/30 transition-colors"
                      onClick={() => handleSortToggle("intakeDate")}
                    >
                      Intake Date{" "}
                      {sortFilter.key === "intakeDate" &&
                        getSortIcon(sortFilter.direction)}
                    </th>
                    <th
                      className="px-6 py-4 text-left text-sm font-semibold text-slate-300 cursor-pointer hover:bg-slate-700/30 transition-colors"
                      onClick={() => handleSortToggle("status")}
                    >
                      Status{" "}
                      {sortFilter.key === "status" &&
                        getSortIcon(sortFilter.direction)}
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">
                      Delete
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/50">
                  {getSortedResidents().map((resident) => (
                    <tr
                      key={resident.id}
                      onClick={() => handleResidentSelect(resident)}
                      className="hover:bg-slate-800/30 transition-colors cursor-pointer"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-cyan-500/10 rounded-lg">
                            <Users className="text-cyan-400 w-4 h-4" />
                          </div>
                          <span className="text-white font-medium">
                            {resident.firstName} {resident.lastName}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-slate-300">
                        {resident.house || "Not Selected"}
                      </td>
                      <td className="px-6 py-4 text-slate-300">
                        {formatPhoneNumber(resident.phoneNumber)}
                      </td>
                      <td className="px-6 py-4 text-slate-300">
                        {resident.referralLocation}
                      </td>
                      <td className="px-6 py-4 text-slate-300">
                        {formatDate(resident.dateAdded)}
                      </td>
                      <td className="px-6 py-4 text-slate-300">
                        {resident.intakeDate
                          ? formatDate(resident.intakeDate)
                          : "Not set"}
                      </td>
                      <td className="px-6 py-4">
                        <div className="ignore-modal-trigger">
                          <select
                            value={resident.status || "pending-followup"}
                            onClick={(e) => e.stopPropagation()} // Stop event from bubbling to the row
                            onChange={(e) =>
                              handleStatusChange(resident.id, e.target.value, e)
                            }
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium ${getStatusColor(
                              resident.status
                            )} bg-opacity-10 border`}
                          >
                            {STATUS_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedResident(resident.id);
                            setDeleteModalOpen(true);
                          }}
                          className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                        >
                          <X className="w-4 h-4 text-red-400" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-4">
            {waitlistResidents.map((resident) => (
              <div
                key={resident.id}
                className="bg-slate-800/50 rounded-lg p-4 border border-slate-700 hover:border-slate-600 transition-all duration-200 cursor-pointer"
                onClick={() => handleResidentSelect(resident)}
              >
                <div className="flex justify-between items-start">
                  <h3 className="text-lg font-medium text-white">
                    {resident.firstName} {resident.lastName}
                  </h3>
                  <select
                    value={resident.status || "pending-followup"}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) =>
                      handleStatusChange(resident.id, e.target.value, e)
                    }
                    className={`px-2 py-1.5 rounded-lg text-sm font-medium ${getStatusColor(
                      resident.status
                    )}`}
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mt-2 text-sm text-slate-300">
                  <p>
                    <strong>House:</strong> {resident.house || "Not Selected"}
                  </p>
                  <p>
                    <strong>Phone:</strong>{" "}
                    {formatPhoneNumber(resident.phoneNumber)}
                  </p>
                  <p>
                    <strong>Referral:</strong> {resident.referralLocation}
                  </p>
                  <p>
                    <strong>Added:</strong> {formatDate(resident.dateAdded)}
                  </p>
                  <p>
                    <strong>Intake Date:</strong>{" "}
                    {resident.intakeDate
                      ? formatDate(resident.intakeDate)
                      : "Not set"}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {isMoveInModalOpen && selectedResident && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex justify-center items-center">
          <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-slate-800">
              <h3 className="text-xl font-semibold text-white">
                {selectedResident.firstName} {selectedResident.lastName} -
                Waitlist Details
              </h3>
            </div>

            {/* Tab Navigation */}
            <div className="flex border-b border-slate-800 overflow-x-auto">
              <button
                onClick={() => setActiveTab("housing")}
                className={`px-6 py-3 transition-colors whitespace-nowrap ${
                  activeTab === "housing"
                    ? "border-b-2 border-cyan-500 text-white"
                    : "text-slate-400 hover:text-slate-300"
                }`}
              >
                Housing
              </button>
              <button
                onClick={() => setActiveTab("personal")}
                className={`px-6 py-3 transition-colors whitespace-nowrap ${
                  activeTab === "personal"
                    ? "border-b-2 border-cyan-500 text-white"
                    : "text-slate-400 hover:text-slate-300"
                }`}
              >
                Personal Info
              </button>
              <button
                onClick={() => setActiveTab("emergency")}
                className={`px-6 py-3 transition-colors whitespace-nowrap ${
                  activeTab === "emergency"
                    ? "border-b-2 border-cyan-500 text-white"
                    : "text-slate-400 hover:text-slate-300"
                }`}
              >
                Emergency Contact
              </button>
              <button
                onClick={() => setActiveTab("medical")}
                className={`px-6 py-3 transition-colors whitespace-nowrap ${
                  activeTab === "medical"
                    ? "border-b-2 border-cyan-500 text-white"
                    : "text-slate-400 hover:text-slate-300"
                }`}
              >
                Medical
              </button>
              <button
                onClick={() => setActiveTab("legal")}
                className={`px-6 py-3 transition-colors whitespace-nowrap ${
                  activeTab === "legal"
                    ? "border-b-2 border-cyan-500 text-white"
                    : "text-slate-400 hover:text-slate-300"
                }`}
              >
                Legal
              </button>
            </div>

            {/* Tab Content Container */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Housing Tab Content */}
              {activeTab === "housing" && (
                <div className="space-y-4">
                  {/* Move In Date */}
                  <div className="relative">
                    <label
                      htmlFor="intakeDate"
                      className="block text-sm font-medium text-slate-300 mb-1"
                    >
                      Move In Date
                    </label>
                    <input
                      type="date"
                      id="intakeDate"
                      value={moveInData.intakeDate}
                      onChange={(e) =>
                        setMoveInData((prev) => ({
                          ...prev,
                          intakeDate: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                      required
                    />
                  </div>

                  {/* House Selection */}
                  <div className="relative">
                    <label
                      htmlFor="house"
                      className="block text-sm font-medium text-slate-300 mb-1"
                    >
                      House
                    </label>
                    <select
                      id="house"
                      value={moveInData.house}
                      onChange={(e) =>
                        setMoveInData((prev) => ({
                          ...prev,
                          house: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                      required
                    >
                      <option value="">Select a house</option>
                      {Object.keys(organizationData?.housesData || {}).map(
                        (houseName) => (
                          <option key={houseName} value={houseName}>
                            {houseName}
                          </option>
                        )
                      )}
                    </select>
                  </div>

                  {/* Rent Schedule */}
                  <div className="relative">
                    <label
                      htmlFor="rentSchedule"
                      className="block text-sm font-medium text-slate-300 mb-1"
                    >
                      Rent Schedule
                    </label>
                    <select
                      id="rentSchedule"
                      value={moveInData.rentSchedule}
                      onChange={(e) => {
                        setMoveInData((prev) => ({
                          ...prev,
                          rentSchedule: e.target.value,
                        }));
                      }}
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                    >
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>

                  {/* Rent Due Date */}
                  <div className="relative">
                    <label
                      htmlFor="rentDueDate"
                      className="block text-sm font-medium text-slate-300 mb-1"
                    >
                      Rent Due Date
                    </label>
                    <input
                      type="date"
                      id="rentDueDate"
                      value={moveInData.rentDueDate}
                      onChange={(e) => {
                        setMoveInData((prev) => ({
                          ...prev,
                          rentDueDate: e.target.value,
                        }));
                      }}
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                      required
                    />
                  </div>

                  {/* Cost Breakdown */}
                  {moveInData.house && (
                    <div className="bg-slate-800/50 rounded-lg text-sm text-slate-300">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          setIsBreakdownOpen(!isBreakdownOpen);
                        }}
                        className="w-full p-4 flex justify-between items-center hover:bg-slate-800/30 transition-colors"
                      >
                        <span className="font-medium">Cost Breakdown</span>
                        <ChevronDown
                          className={`w-5 h-5 transition-transform ${
                            isBreakdownOpen ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                      {isBreakdownOpen && (
                        <div className="p-4 pt-4 space-y-2 border-t border-slate-700">
                          <p>
                            Move-in Fee: $
                            {calculationDetails?.breakdown?.moveInFee?.toFixed(
                              2
                            ) || "0.00"}
                          </p>
                          {calculationDetails?.breakdown && (
                            <>
                              <p>
                                Daily Rate: $
                                {calculationDetails.breakdown.dailyRate.toFixed(
                                  2
                                )}
                              </p>
                              <p>
                                Prorated Rent: $
                                {calculationDetails.breakdown.proratedRent.toFixed(
                                  2
                                )}
                              </p>
                              <p>
                                Days Until Due:{" "}
                                {calculationDetails.breakdown.daysUntilDue}
                              </p>
                              <p>
                                Next Due Date:{" "}
                                {calculationDetails.breakdown.nextDueDate.toLocaleDateString()}
                              </p>
                              {calculationDetails.breakdown.isFullPeriod && (
                                <p className="text-cyan-400">
                                  Full{" "}
                                  {
                                    organizationData?.houseSettings
                                      ?.rentSchedule
                                  }{" "}
                                  period
                                </p>
                              )}
                            </>
                          )}
                          <p className="text-white font-semibold">
                            Total Due: $
                            {Math.abs(
                              calculationDetails?.totalBalance || 0
                            ).toFixed(2)}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Starting Balance */}
                  <div className="relative">
                    <label
                      htmlFor="balance"
                      className="block text-sm font-medium text-slate-300 mb-1"
                    >
                      Starting Balance
                    </label>
                    <input
                      type="number"
                      id="balance"
                      value={moveInData.balance}
                      onChange={(e) =>
                        setMoveInData((prev) => ({
                          ...prev,
                          balance: parseFloat(e.target.value) || 0,
                        }))
                      }
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                      required
                    />
                  </div>

                  {/* UA Count */}
                  <div className="relative">
                    <label
                      htmlFor="uaCount"
                      className="block text-sm font-medium text-slate-300 mb-1"
                    >
                      UA Count
                    </label>
                    <input
                      type="number"
                      id="uaCount"
                      value={moveInData.uaCount}
                      onChange={(e) =>
                        setMoveInData((prev) => ({
                          ...prev,
                          uaCount: parseInt(e.target.value, 10) || 0,
                        }))
                      }
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                      required
                    />
                  </div>
                </div>
              )}

              {/* Personal Info Tab Content */}
              {activeTab === "personal" && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* First Name */}
                    <div className="relative">
                      <label
                        htmlFor="firstName"
                        className="block text-sm font-medium text-slate-300 mb-1"
                      >
                        First Name
                      </label>
                      <input
                        type="text"
                        id="firstName"
                        value={selectedResident.firstName || ""}
                        onChange={(e) =>
                          handleFieldChange("firstName", e.target.value)
                        }
                        className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                      />
                    </div>

                    {/* Last Name */}
                    <div className="relative">
                      <label
                        htmlFor="lastName"
                        className="block text-sm font-medium text-slate-300 mb-1"
                      >
                        Last Name
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        value={selectedResident.lastName || ""}
                        onChange={(e) =>
                          handleFieldChange("lastName", e.target.value)
                        }
                        className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                      />
                    </div>
                  </div>

                  {/* Gender */}
                  <div className="relative">
                    <label
                      htmlFor="gender"
                      className="block text-sm font-medium text-slate-300 mb-1"
                    >
                      Gender
                    </label>
                    <select
                      id="gender"
                      value={selectedResident.gender || ""}
                      onChange={(e) =>
                        handleFieldChange("gender", e.target.value)
                      }
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                    >
                      <option value="">Select gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Trans">Trans</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  {/* Date of Birth */}
                  <div className="relative">
                    <label
                      htmlFor="dob"
                      className="block text-sm font-medium text-slate-300 mb-1"
                    >
                      Date of Birth
                    </label>
                    <input
                      type="date"
                      id="dob"
                      value={
                        selectedResident.dob && selectedResident.dob.toDate
                          ? formatDateForInput(selectedResident.dob.toDate())
                          : ""
                      }
                      onChange={(e) => handleFieldChange("dob", e.target.value)}
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                    />
                  </div>

                  {/* Phone Number */}
                  <div className="relative">
                    <label
                      htmlFor="phoneNumber"
                      className="block text-sm font-medium text-slate-300 mb-1"
                    >
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phoneNumber"
                      value={selectedResident.phoneNumber || ""}
                      onChange={(e) => {
                        // Keep only digits
                        const rawValue = e.target.value.replace(/\D/g, "");
                        handleFieldChange("phoneNumber", rawValue);
                      }}
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                    />
                  </div>

                  {/* Email */}
                  <div className="relative">
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-slate-300 mb-1"
                    >
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={selectedResident.email || ""}
                      onChange={(e) =>
                        handleFieldChange("email", e.target.value)
                      }
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                    />
                  </div>

                  {/* Veteran Status */}
                  <div className="relative">
                    <label
                      htmlFor="veteranStatus"
                      className="block text-sm font-medium text-slate-300 mb-1"
                    >
                      Veteran Status
                    </label>
                    <select
                      id="veteranStatus"
                      value={selectedResident.veteranStatus || ""}
                      onChange={(e) =>
                        handleFieldChange("veteranStatus", e.target.value)
                      }
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                    >
                      <option value="">Select veteran status</option>
                      <option value="Veteran">Veteran</option>
                      <option value="No military background">
                        No military background
                      </option>
                    </select>
                  </div>

                  {/* Work on Entry */}
                  <div className="relative">
                    <label
                      htmlFor="workOnEntry"
                      className="block text-sm font-medium text-slate-300 mb-1"
                    >
                      Work on Entry
                    </label>
                    <select
                      id="workOnEntry"
                      value={selectedResident.workOnEntry || ""}
                      onChange={(e) =>
                        handleFieldChange("workOnEntry", e.target.value)
                      }
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                    >
                      <option value="">Select work status on entry</option>
                      <option value="Unemployed">Unemployed</option>
                      <option value="Part-Time">Part-Time</option>
                      <option value="Full-Time">Full-Time</option>
                      <option value="Retired">Retired</option>
                    </select>
                  </div>
                </div>
              )}

              {/* Emergency Contact Tab */}
              {activeTab === "emergency" && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* EC First Name */}
                    <div className="relative">
                      <label
                        htmlFor="ecFirstName"
                        className="block text-sm font-medium text-slate-300 mb-1"
                      >
                        First Name
                      </label>
                      <input
                        type="text"
                        id="ecFirstName"
                        value={selectedResident.ecFirstName || ""}
                        onChange={(e) =>
                          handleFieldChange("ecFirstName", e.target.value)
                        }
                        className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                      />
                    </div>

                    {/* EC Last Name */}
                    <div className="relative">
                      <label
                        htmlFor="ecLastName"
                        className="block text-sm font-medium text-slate-300 mb-1"
                      >
                        Last Name
                      </label>
                      <input
                        type="text"
                        id="ecLastName"
                        value={selectedResident.ecLastName || ""}
                        onChange={(e) =>
                          handleFieldChange("ecLastName", e.target.value)
                        }
                        className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                      />
                    </div>
                  </div>

                  {/* EC Relationship */}
                  <div className="relative">
                    <label
                      htmlFor="ecRelationship"
                      className="block text-sm font-medium text-slate-300 mb-1"
                    >
                      Relationship
                    </label>
                    <input
                      type="text"
                      id="ecRelationship"
                      value={selectedResident.ecRelationship || ""}
                      onChange={(e) =>
                        handleFieldChange("ecRelationship", e.target.value)
                      }
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                    />
                  </div>

                  {/* EC Phone Number */}
                  <div className="relative">
                    <label
                      htmlFor="ecPhoneNumber"
                      className="block text-sm font-medium text-slate-300 mb-1"
                    >
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="ecPhoneNumber"
                      value={selectedResident.ecPhoneNumber || ""}
                      onChange={(e) => {
                        // Keep only digits
                        const rawValue = e.target.value.replace(/\D/g, "");
                        handleFieldChange("ecPhoneNumber", rawValue);
                      }}
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                    />
                  </div>

                  {/* EC Email */}
                  <div className="relative">
                    <label
                      htmlFor="ecEmail"
                      className="block text-sm font-medium text-slate-300 mb-1"
                    >
                      Email
                    </label>
                    <input
                      type="email"
                      id="ecEmail"
                      value={selectedResident.ecEmail || ""}
                      onChange={(e) =>
                        handleFieldChange("ecEmail", e.target.value)
                      }
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                    />
                  </div>
                </div>
              )}

              {/* Medical Tab */}
              {activeTab === "medical" && (
                <div className="space-y-4">
                  {/* Referral Location */}
                  <div className="relative">
                    <label
                      htmlFor="referralLocation"
                      className="block text-sm font-medium text-slate-300 mb-1"
                    >
                      Referral Location
                    </label>
                    <select
                      id="referralLocation"
                      value={selectedResident.referralLocation || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "add_new_option") {
                          // Open the modal to add a new referral
                          setIsModalOpen(true);
                          return;
                        }
                        handleFieldChange("referralLocation", value);
                      }}
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                    >
                      <option value="">Select a referral location</option>
                      {referralOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                      <option
                        value="add_new_option"
                        className="text-slate-400 italic"
                        style={{ fontStyle: "italic", color: "#94a3b8" }}
                      >
                        + Add new referral source
                      </option>
                    </select>
                  </div>

                  {/* Custom Referral Location if "Other" is selected */}
                  {selectedResident.referralLocation === "Other" && (
                    <div className="relative">
                      <label
                        htmlFor="customReferralLocation"
                        className="block text-sm font-medium text-slate-300 mb-1"
                      >
                        Custom Referral Location
                      </label>
                      <input
                        type="text"
                        id="customReferralLocation"
                        value={selectedResident.customReferralLocation || ""}
                        onChange={(e) => {
                          handleFieldChange(
                            "customReferralLocation",
                            e.target.value
                          );
                        }}
                        onBlur={(e) => {
                          if (e.target.value.trim()) {
                            handleAddReferralOption(e.target.value.trim());
                          }
                        }}
                        className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                        placeholder="Enter custom referral location"
                      />
                    </div>
                  )}

                  {/* Drug of Choice */}
                  <div className="relative">
                    <label
                      htmlFor="drugOfChoice"
                      className="block text-sm font-medium text-slate-300 mb-1"
                    >
                      Drug of Choice
                    </label>
                    <input
                      type="text"
                      id="drugOfChoice"
                      value={selectedResident.drugOfChoice || ""}
                      onChange={(e) =>
                        handleFieldChange("drugOfChoice", e.target.value)
                      }
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                    />
                    <p className="text-xs text-slate-400 mt-1">
                      Separate multiple choices with commas
                    </p>
                  </div>

                  {/* Sobriety Date */}
                  <div className="relative">
                    <label
                      htmlFor="sobrietyDate"
                      className="block text-sm font-medium text-slate-300 mb-1"
                    >
                      Sobriety Date
                    </label>
                    <input
                      type="date"
                      id="sobrietyDate"
                      value={
                        selectedResident.sobrietyDate &&
                        selectedResident.sobrietyDate.toDate
                          ? formatDateForInput(
                              selectedResident.sobrietyDate.toDate()
                            )
                          : ""
                      }
                      onChange={(e) =>
                        handleFieldChange("sobrietyDate", e.target.value)
                      }
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                    />
                  </div>

                  {/* MAT Status */}
                  <div className="relative">
                    <label
                      htmlFor="isMAT"
                      className="block text-sm font-medium text-slate-300 mb-1"
                    >
                      On MAT (Medication-Assisted Treatment)?
                    </label>
                    <select
                      id="isMAT"
                      value={selectedResident.isMAT ? "Yes" : "No"}
                      onChange={(e) =>
                        handleFieldChange("isMAT", e.target.value === "Yes")
                      }
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                    >
                      <option value="No">No</option>
                      <option value="Yes">Yes</option>
                    </select>
                  </div>

                  {/* Medications */}
                  <div className="space-y-2">
                    {selectedResident.medications &&
                    selectedResident.medications.length > 0 ? (
                      selectedResident.medications.map((med, idx) => (
                        <div key={idx} className="grid grid-cols-2 gap-2">
                          <div className="relative">
                            <input
                              type="text"
                              placeholder="Medication name"
                              value={med.name || ""}
                              onChange={(e) =>
                                handleMedicationNameInput(e, idx)
                              }
                              onBlur={() =>
                                setTimeout(() => {
                                  setMedicationSuggestions([]);
                                  setActiveMedicationIndex(null);
                                }, 200)
                              }
                              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                            />

                            {/* Medication Suggestions Dropdown */}
                            {medicationSuggestions.length > 0 &&
                              activeMedicationIndex === idx && (
                                <div className="absolute z-40 mt-1 w-full bg-slate-800 border border-slate-700 rounded-lg shadow-lg">
                                  {medicationSuggestions.map(
                                    (suggestion, sIdx) => (
                                      <div
                                        key={sIdx}
                                        onClick={() =>
                                          handleSelectSuggestion(
                                            suggestion,
                                            idx
                                          )
                                        }
                                        className="px-4 py-2 hover:bg-slate-700 cursor-pointer text-white"
                                      >
                                        <div className="font-semibold">
                                          {suggestion.brandName} (
                                          {suggestion.genericName})
                                        </div>
                                        <div className="text-sm text-slate-400">
                                          {suggestion.type}
                                        </div>
                                      </div>
                                    )
                                  )}
                                </div>
                              )}
                          </div>

                          <input
                            type="text"
                            placeholder="Dosage"
                            value={med.dose || ""}
                            onChange={(e) => {
                              const updatedMeds = [
                                ...selectedResident.medications,
                              ];
                              updatedMeds[idx] = {
                                ...med,
                                dose: e.target.value,
                              };
                              handleFieldChange("medications", updatedMeds);
                            }}
                            className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                          />
                        </div>
                      ))
                    ) : (
                      <p className="text-slate-400 italic">
                        No medications listed
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        const updatedMeds = [
                          ...(selectedResident.medications || []),
                          { name: "", dose: "" },
                        ];
                        handleFieldChange("medications", updatedMeds);
                      }}
                      className="mt-2 px-3 py-1 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-sm"
                    >
                      + Add Medication
                    </button>
                  </div>
                </div>
              )}

              {/* Legal Tab */}
              {activeTab === "legal" && (
                <div className="space-y-4">
                  {/* PO First Name */}
                  <div className="relative">
                    <label
                      htmlFor="poFirstName"
                      className="block text-sm font-medium text-slate-300 mb-1"
                    >
                      PO First Name
                    </label>
                    <input
                      type="text"
                      id="poFirstName"
                      value={selectedResident.poFirstName || ""}
                      onChange={(e) =>
                        handleFieldChange("poFirstName", e.target.value)
                      }
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                    />
                  </div>

                  {/* PO Last Name */}
                  <div className="relative">
                    <label
                      htmlFor="poLastName"
                      className="block text-sm font-medium text-slate-300 mb-1"
                    >
                      PO Last Name
                    </label>
                    <input
                      type="text"
                      id="poLastName"
                      value={selectedResident.poLastName || ""}
                      onChange={(e) =>
                        handleFieldChange("poLastName", e.target.value)
                      }
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                    />
                  </div>

                  {/* PO Email */}
                  <div className="relative">
                    <label
                      htmlFor="poEmail"
                      className="block text-sm font-medium text-slate-300 mb-1"
                    >
                      PO Email
                    </label>
                    <input
                      type="email"
                      id="poEmail"
                      value={selectedResident.poEmail || ""}
                      onChange={(e) =>
                        handleFieldChange("poEmail", e.target.value)
                      }
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                    />
                  </div>

                  {/* County */}
                  <div className="relative">
                    <label
                      htmlFor="county"
                      className="block text-sm font-medium text-slate-300 mb-1"
                    >
                      County
                    </label>
                    <input
                      type="text"
                      id="county"
                      value={selectedResident.county || ""}
                      onChange={(e) =>
                        handleFieldChange("county", e.target.value)
                      }
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                    />
                  </div>

                  {/* Pending Charges */}
                  <div className="relative">
                    <label
                      htmlFor="pendingCharges"
                      className="block text-sm font-medium text-slate-300 mb-1"
                    >
                      Pending Charges
                    </label>
                    <textarea
                      id="pendingCharges"
                      value={selectedResident.pendingCharges || ""}
                      onChange={(e) =>
                        handleFieldChange("pendingCharges", e.target.value)
                      }
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors min-h-[100px]"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer with Actions */}
            <div className="px-6 py-4 border-t border-slate-700">
              {/* User Creation Checkbox - only show if email exists */}
              {selectedResident.email && (
                <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/50 mb-4">
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="createUserAccount"
                      checked={createUserAccount}
                      onChange={(e) => setCreateUserAccount(e.target.checked)}
                      className="w-4 h-4 text-cyan-600 bg-slate-800 border-slate-600 rounded focus:ring-cyan-500 focus:ring-2"
                    />
                    <label
                      htmlFor="createUserAccount"
                      className="text-sm font-medium text-slate-300 cursor-pointer"
                    >
                      Add this resident as a user
                    </label>
                  </div>
                  <p className="text-xs text-slate-400 mt-2 ml-7">
                    This will create a user account for{" "}
                    {selectedResident.firstName} {selectedResident.lastName}{" "}
                    with email: {selectedResident.email}
                  </p>
                  <p className="text-xs text-slate-500 mt-1 ml-7">
                    Current resident users:{" "}
                    {users.filter((u) => u.role === "Resident").length} /{" "}
                    {residentDashboardCount}
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsMoveInModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white bg-slate-800 hover:bg-slate-700 rounded-lg border border-slate-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleUpdate}
                  className="px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors"
                >
                  Update Info
                </button>
                <button
                  type="button"
                  onClick={handleMoveIn}
                  className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 hover:bg-cyan-700 rounded-lg transition-colors"
                >
                  Move In
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {deleteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex justify-center items-center">
          <div className="bg-slate-900/90 border border-slate-700 rounded-xl shadow-lg w-full max-w-lg">
            <div className="px-6 py-4 border-b border-slate-700">
              <h3 className="text-xl font-semibold text-white">
                Delete Resident
              </h3>
            </div>
            <div className="p-6">
              <p className="text-slate-300 text-center">
                Are you sure you want to remove this resident from the system?
              </p>
              <div className="flex justify-end gap-4 mt-6">
                <button
                  onClick={() => setDeleteModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white bg-slate-800 rounded-lg border border-slate-700 hover:bg-slate-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {isModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-[200]">
          <div className="bg-slate-900 p-6 rounded-lg shadow-lg max-w-md w-full border border-slate-800">
            <h2 className="text-white text-lg font-semibold mb-4">
              Add New Referral Source
            </h2>
            <input
              type="text"
              value={newReferral}
              onChange={(e) => setNewReferral(e.target.value)}
              className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              placeholder="Enter new referral source"
            />
            <div className="flex justify-end gap-4 mt-4">
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  setNewReferral(""); // Clear input
                }}
                className="px-4 py-2 bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700 border border-slate-700 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleAddReferralOption(newReferral);
                  setIsModalOpen(false);
                  setNewReferral("");
                }}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-all"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Waitlist;
